import { Tooltip } from 'antd'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { API } from '@/api'
import { Loading, Trans } from '@/components'
import { Pagination } from '@/components/base/Pagination'
import {
  BILL_SUBTYPE_DISPLAY,
  BILL_TYPE_DISPLAY,
  BILL_TYPE_SUBTYPE_DISPLAY,
  isAssetTransferHold,
} from '@/models/bill'
import { getBillDisplayTimeSeconds } from '@/utils/billingTime'
import { formatDatePreset, formatPrice } from '@/utils/formatter'

export function InvoicePage() {
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(1)
  const [total, setTotal] = useState(0)
  const [bills, setBills] = useState<any[]>([])
  const pageSize = 10
  const { t } = useTranslation('billing')

  const fetchBill = async (p = page) => {
    setLoading(true)
    try {
      const res = await API.getUserBills({
        page: p,
        per_page: pageSize,
        catalog: 0,
      })
      if (res.code === 0) {
        setBills(
          res.bills?.map((b: any) => {
            return {
              ...b,
              isUnsettled: b.type === 1,
              isHold: isAssetTransferHold(b),
              typeStr: (() => {
                const rawTypeKey =
                  BILL_TYPE_SUBTYPE_DISPLAY[`${b.type}-${b.subType}`] ||
                  BILL_SUBTYPE_DISPLAY[b.subType] ||
                  BILL_TYPE_DISPLAY[b.type] ||
                  '-'
                return t(`typeStr.${rawTypeKey}`, { defaultValue: rawTypeKey })
              })(),
            }
          }) || [],
        )
        setPage(p)
        setTotal(res.pagination.total)
      }
    } catch (error) {
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchBill()
  }, [])

  return (
    <div>
      <div className="mt-2.5 card bg-panel-white">
        <div className="flex items-center gap-x-8 h6">
          <h6 className="text-primary">
            <Trans i18nKey="profile.invoiceTab">Invoice</Trans>
          </h6>
          {/* <h6 className="text-tertiary">
            <Trans i18nKey="profile.invoiceTab">Invoice</Trans>
          </h6> */}
        </div>
        <div className="my-8 border-b border-gray100"></div>

        {loading ? (
          <div className="w-full flex items-center justify-center p-8">
            <Loading />
          </div>
        ) : (
          <>
            <table className="table mt-4">
              <thead>
                <tr>
                  <th>
                    <Trans i18nKey="profile.invoiceTableHeaderType">Time</Trans>
                  </th>
                  <th>
                    <Trans i18nKey="profile.invoiceTableHeaderName">Name</Trans>
                  </th>
                  <th>
                    <Trans i18nKey="profile.invoiceTableHeaderStatus">Status</Trans>
                  </th>

                  <th className="!text-right">
                    <Trans i18nKey="profile.invoiceTableHeaderPayment">Payment</Trans>
                  </th>
                </tr>
              </thead>
              <tbody>
                {bills.map((data, idx) => (
                  <tr key={idx}>
                    <td className="t-sm">
                      <div className="t-xs text-secondary">
                        {formatDatePreset((getBillDisplayTimeSeconds(data) || 0) * 1000, 'minute')}
                      </div>
                    </td>
                    <td className="t-sm">
                      <div className="text-error">{data.typeStr}</div>
                    </td>
                    <td className="t-sm text-tertiary">
                      {data.isHold ? (
                        <Tooltip title={t('status.holdHint')} placement="top" mouseEnterDelay={0}>
                          <span className="cursor-help underline decoration-dotted">
                            {t('status.hold')}
                          </span>
                        </Tooltip>
                      ) : data.isUnsettled ? (
                        t('status.ongoing')
                      ) : (
                        t('status.settled')
                      )}
                    </td>
                    <td
                      className={data.isHold ? 't-sm text-right text-tertiary' : 't-sm text-right'}
                    >
                      {formatPrice(data.amount)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Pagination
              size={pageSize}
              total={total}
              current={page}
              className="justify-end mt-4"
              onChange={(page) => fetchBill(page)}
            />
          </>
        )}
      </div>
    </div>
  )
}
